import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  getCustomers, getSales, getInstallmentsBySaleId, getPromissoryNoteById,
  updateEnforcementCase, getEnforcementCaseById,
  getPromissoryNotesByCustomerId, recordUnifiedPayment, syncPaymentPromisesAfterPayment,
  getCustomerOpenInstallments,
} from '@/lib/supabaseDb';
import { useToast } from '@/components/feature/Toast';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import ResponsiveFormModal from '@/components/base/ResponsiveFormModal';
import CurrencyInput from '@/components/base/CurrencyInput';
import CustomerSearchSelect from '@/components/feature/CustomerSearchSelect';
import { formatTRY } from '@/lib/currencyFormat';
import Badge from '@/components/base/Badge';
import { installmentStatus } from '@/lib/statusDefs';

// Backend zaten anlamlı bir Türkçe hata döndürdüyse (ör. "Ödeme tutarı kalan
// senet borcundan fazla olamaz") onu aynen gösteririz; ancak ham bir Postgres/
// Supabase teknik hatası sızarsa (constraint/anahtar/SQL ifadesi içeren
// mesajlar) kullanıcıya bunun yerine anlaşılır, genel bir mesaj gösteririz.
function toFriendlyPaymentError(raw: string): string {
  const technical = /constraint|duplicate key|violates|column|relation|syntax error|23[0-9]{3}|null value/i;
  if (technical.test(raw)) {
    return 'İşlem gerçekleştirilemedi. Girilen tutarı ve seçili kaydı kontrol edip tekrar deneyin.';
  }
  return raw;
}

// ─── Props ──────────────────────────────────────────────────

export type PaymentFormMode = 'general' | 'customer' | 'sale' | 'note' | 'promise';

export interface PaymentFormModalProps {
  open: boolean;
  onClose: () => void;
  mode: PaymentFormMode;
  /** Customer pre-filled & locked for customer/sale/note/promise modes */
  customerId?: string;
  /** Sale pre-filled & locked for sale/promise modes */
  saleId?: string;
  /** Note pre-filled for note mode */
  noteId?: string;
  /** Pre-filled amount (e.g. from promise) */
  initialAmount?: number;
  /** Enforcement case to update after note payment */
  enforcementCaseId?: string;
  /** Called after successful payment */
  onPaymentCreated?: () => void;
  /** Render as full page instead of modal */
  asPage?: boolean;
}

// ─── Component ──────────────────────────────────────────────

export default function PaymentFormModal({
  open, onClose, mode, customerId: preCustomerId, saleId: preSaleId,
  noteId, initialAmount, enforcementCaseId, onPaymentCreated, asPage,
}: PaymentFormModalProps) {
  const { showToast } = useToast();
  const { triggerRefresh } = useDataRefresh();

  // ─── Data ───────────────────────────────────────────────
  const [customers, setCustomers] = useState<any[]>([]);
  const [allSales, setAllSales] = useState<any[]>([]);
  const [installments, setInstallments] = useState<any[]>([]);
  const [note, setNote] = useState<any>(null);
  const [customerNotes, setCustomerNotes] = useState<any[]>([]);
  const [customerPlanInstallments, setCustomerPlanInstallments] = useState<any[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  // ─── Form state ─────────────────────────────────────────
  const [customerId, setCustomerId] = useState('');
  const [saleId, setSaleId] = useState('');
  const [sourceType, setSourceType] = useState<'sale' | 'note' | 'legacy_debt' | 'installment_plan'>('sale');
  const [selectedNoteId, setSelectedNoteId] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState('Nakit');
  const [description, setDescription] = useState('');
  const [receivedBy, setReceivedBy] = useState('Admin');
  const [selectedInstallmentIds, setSelectedInstallmentIds] = useState<string[]>([]);
  const [customAmount, setCustomAmount] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showOverrideWarning, setShowOverrideWarning] = useState(false);

  const methods = ['Nakit', 'Kredi Kartı', 'Havale', 'EFT', 'Senet', 'Diğer'];
  const personnel = ['Admin', 'Ahmet Satış'];

  const isLockedCustomer = mode !== 'general';
  const isLockedSale = mode === 'sale' || mode === 'promise';

  // ─── Load data when modal opens ─────────────────────────
  useEffect(() => {
    if (!open || dataLoaded) return;
    async function load() {
      setLoading(true);
      try {
        const promises: Promise<any>[] = [getCustomers(), getSales()];
        if (noteId) promises.push(getPromissoryNoteById(noteId));
        const results = await Promise.all(promises);
        setCustomers((results[0].data || []) as any[]);
        setAllSales((results[1].data || []) as any[]);
        if (noteId && results[2]) setNote(results[2].data);
        setDataLoaded(true);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    }
    load();
  }, [open, noteId, dataLoaded]);

  // ─── Pre-fill from props ────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (preCustomerId) setCustomerId(preCustomerId);
    if (preSaleId) setSaleId(preSaleId);
    if (initialAmount && initialAmount > 0) setCustomAmount(initialAmount);
  }, [open, preCustomerId, preSaleId, initialAmount]);

  // ─── Derived ────────────────────────────────────────────
  const selectedCustomer = useMemo(() => customers.find(c => c.id === (isLockedCustomer ? preCustomerId : customerId)), [customers, customerId, preCustomerId, isLockedCustomer]);

  const effectiveCustomerId = isLockedCustomer ? (preCustomerId || '') : customerId;

  // Satışlar bir senede bağlıysa (sales.promissory_note_id ya da
  // promissory_notes.sale_id üzerinden), ödeme mantığı senet üzerinden
  // ilerliyor demektir (bkz. get_customer_debt_components / update_sale_transaction
  // 3-yönlü bağlantı dışlaması) — bu satışlar "Satış" kartına dahil edilmez,
  // aksi halde aynı borç hem satıştan hem senetten ödenebilir gibi görünür.
  const noteLinkedSaleIds = useMemo(() => {
    const activeNoteIds = new Set(customerNotes.map((n: any) => n.id));
    const ids = new Set<string>();
    customerNotes.forEach((n: any) => { if (n.sale_id) ids.add(n.sale_id); });
    allSales.forEach(s => { if (s.promissory_note_id && activeNoteIds.has(s.promissory_note_id)) ids.add(s.id); });
    return ids;
  }, [customerNotes, allSales]);

  const customerSales = useMemo(
    () => allSales.filter(s =>
      s.customer_id === effectiveCustomerId &&
      s.payment_status !== 'İptal' &&
      s.remaining_amount > 0 &&
      !noteLinkedSaleIds.has(s.id)
    ),
    [allSales, effectiveCustomerId, noteLinkedSaleIds]
  );

  // Eski sistem/devir borcu: source_type='legacy_opening_balance' olan açık senetler
  const legacyNotes = useMemo(
    () => customerNotes.filter((n: any) => n.source_type === 'legacy_opening_balance'),
    [customerNotes]
  );
  const legacyDebtAmount = useMemo(
    () => legacyNotes.reduce((sum: number, n: any) =>
      sum + (n.remaining_amount !== undefined ? n.remaining_amount : ((n.amount || 0) - (n.paid_amount || 0))), 0),
    [legacyNotes]
  );
  const hasLegacyDebt = legacyDebtAmount > 0;

  const hasAnyPayableSource = customerSales.length > 0 || customerNotes.length > 0 || customerPlanInstallments.length > 0 || hasLegacyDebt;

  const selectedSale = useMemo(() => {
    if (isLockedSale && preSaleId) return allSales.find(s => s.id === preSaleId);
    if (noteId && note) {
      return allSales.find(s => s.id === (note as any).sale_id);
    }
    return allSales.find(s => s.id === saleId);
  }, [allSales, saleId, preSaleId, isLockedSale, noteId, note]);

  const effectiveSaleId = isLockedSale ? (preSaleId || '') : (noteId && note ? ((note as any).sale_id || '') : saleId);

  const isTaksitli = selectedSale?.payment_type === 'Taksitli';
  const isNoteMode = mode === 'note' || !!noteId;

  // Source type is visible only in general/customer modes
  const showSourceSelector = mode === 'general' || mode === 'customer';

  // ─── Load customer notes + plan installments when customer changes ──────────
  const autoPickedForCustomer = useRef<string>('');
  // NOT: sourceDataLoading/loading booleanları effect-sırası yüzünden ilk mount
  // anında henüz "true"ya çevrilmeden aşağıdaki auto-pick effect'i yanlışlıkla
  // "veri hazır" sanabiliyordu (React aynı commit içindeki effect'ler arasında
  // bir önceki effect'in setState'ini senkron yansıtmaz — auto-pick effect
  // hâlâ önceki render'ın "false" değerini görüyordu). Bunun yerine, bu
  // customerId için fetch'in GERÇEKTEN tamamlandığını işaretleyen ayrı,
  // belirsizliksiz bir reactive state kullanıyoruz (dataLoaded'ın aynısı,
  // müşteri notları/taksitleri için).
  const [sourceDataReady, setSourceDataReady] = useState(false);
  useEffect(() => {
    if (!effectiveCustomerId || isNoteMode) {
      setCustomerNotes([]);
      setSelectedNoteId('');
      setCustomerPlanInstallments([]);
      return;
    }
    autoPickedForCustomer.current = '';
    setSourceDataReady(false);
    Promise.all([
      getPromissoryNotesByCustomerId(effectiveCustomerId),
      getCustomerOpenInstallments(effectiveCustomerId),
    ]).then(([notesRes, instRes]) => {
      const notes = (notesRes.data || []) as any[];
      // Only open notes (not paid, not cancelled)
      const openNotes = notes.filter((n: any) => n.status !== 'Ödendi' && n.status !== 'İptal');
      setCustomerNotes(openNotes);
      setSelectedNoteId('');

      const allInst = (instRes.data || []) as any[];
      setCustomerPlanInstallments(allInst);
      setSourceDataReady(true);
    });
  }, [effectiveCustomerId, isNoteMode]);

  // ─── Varsayılan ödeme kaynağı seçimi: Satış > Senet > Taksit Planı > Eski Sistem Borcu ───
  // Müşteri verisi (açık satış/senet/taksit/devir borcu) yüklendikten sonra bir kez
  // otomatik seçilir; kullanıcının sonradan yaptığı manuel seçimi ezmez.
  useEffect(() => {
    if (!showSourceSelector || !effectiveCustomerId || !dataLoaded || !sourceDataReady) return;
    if (autoPickedForCustomer.current === effectiveCustomerId) return;
    if (customerSales.length > 0) {
      setSourceType('sale');
      autoPickedForCustomer.current = effectiveCustomerId;
    } else if (customerNotes.length > 0) {
      setSourceType('note');
      autoPickedForCustomer.current = effectiveCustomerId;
    } else if (customerPlanInstallments.length > 0) {
      setSourceType('installment_plan');
      autoPickedForCustomer.current = effectiveCustomerId;
    } else if (hasLegacyDebt) {
      setSourceType('legacy_debt');
      autoPickedForCustomer.current = effectiveCustomerId;
    } else {
      // Ödenebilir açık kayıt yok — kartlar disabled kalacak, uyarı gösterilecek
      autoPickedForCustomer.current = effectiveCustomerId;
    }
  }, [showSourceSelector, effectiveCustomerId, dataLoaded, sourceDataReady, customerSales.length, customerNotes.length, customerPlanInstallments.length, hasLegacyDebt]);

  // ─── Selected note derived ─────────────────────────────
  const selectedSourceNote = useMemo(() => {
    if (isNoteMode && note) return note;
    if (sourceType === 'note' && selectedNoteId) {
      return customerNotes.find(n => n.id === selectedNoteId) || null;
    }
    return null;
  }, [isNoteMode, note, sourceType, selectedNoteId, customerNotes]);

  const sourceNoteRemaining = useMemo(() => {
    if (!selectedSourceNote) return 0;
    return (selectedSourceNote as any).remaining_amount !== undefined
      ? (selectedSourceNote as any).remaining_amount
      : (((selectedSourceNote as any).amount || 0) - ((selectedSourceNote as any).paid_amount || 0));
  }, [selectedSourceNote]);

  const isSourceNote = (isNoteMode) || (sourceType === 'note' && !!selectedNoteId);
  const isSourceLegacyDebt = !isNoteMode && sourceType === 'legacy_debt';
  const isSourceInstallmentPlan = !isNoteMode && sourceType === 'installment_plan';

  // ─── Plan installments derived ────────────────────────
  const planOpenCount = customerPlanInstallments.length;
  const planActiveInstallments = useMemo(
    () => customerPlanInstallments.filter(i => i.status !== 'Ödendi' && i.status !== 'İptal'),
    [customerPlanInstallments]
  );
  const plansortedInstallments = useMemo(() =>
    [...customerPlanInstallments].sort((a, b) => {
      // Overdue first, then by due_date, then sequence
      if (a.status === 'Gecikti' && b.status !== 'Gecikti') return -1;
      if (a.status !== 'Gecikti' && b.status === 'Gecikti') return 1;
      return (a.due_date || '').localeCompare(b.due_date || '') || (a.sequence_no || 0) - (b.sequence_no || 0);
    }),
  [customerPlanInstallments]);

  const planSelectedRemainingTotal = useMemo(() => {
    if (!isSourceInstallmentPlan) return 0;
    return customerPlanInstallments
      .filter(i => selectedInstallmentIds.includes(i.id))
      .reduce((sum, i) => sum + (i.remaining_amount !== undefined ? i.remaining_amount : (i.amount - (i.paid_amount || 0))), 0);
  }, [customerPlanInstallments, selectedInstallmentIds, isSourceInstallmentPlan]);

  const planOverdueTotal = useMemo(() => {
    return planActiveInstallments
      .filter(i => i.status === 'Gecikti')
      .reduce((sum, i) => sum + (i.remaining_amount !== undefined ? i.remaining_amount : (i.amount - (i.paid_amount || 0))), 0);
  }, [planActiveInstallments]);

  // ─── Load installments ─────────────────────────────────
  useEffect(() => {
    const sid = effectiveSaleId;
    if (!sid || !selectedSale || !isTaksitli) {
      setInstallments([]);
      setSelectedInstallmentIds([]);
      return;
    }
    getInstallmentsBySaleId(sid).then(res => {
      setInstallments((res.data || []) as any[]);
      setSelectedInstallmentIds([]);
    });
  }, [effectiveSaleId, selectedSale, isTaksitli]);

  const activeInstallments = useMemo(
    () => installments.filter(i => i.status !== 'Ödendi' && i.status !== 'İptal'),
    [installments]
  );

  const sortedInstallments = useMemo(() =>
    [...installments].sort((a, b) => (a.sequence_no || 0) - (b.sequence_no || 0) || (a.due_date || '').localeCompare(b.due_date || '')),
  [installments]);

  const selectedRemainingTotal = useMemo(() => {
    if (!isTaksitli) return 0;
    return installments
      .filter(i => selectedInstallmentIds.includes(i.id))
      .reduce((sum, i) => sum + (i.remaining_amount !== undefined ? i.remaining_amount : (i.amount - (i.paid_amount || 0))), 0);
  }, [installments, selectedInstallmentIds, isTaksitli]);

  const effectiveAmount = useMemo(() => {
    if (customAmount !== null) return customAmount;
    if (isSourceInstallmentPlan) return planSelectedRemainingTotal;
    if (isTaksitli) return selectedRemainingTotal;
    if (isSourceNote) return sourceNoteRemaining;
    return 0;
  }, [customAmount, isSourceInstallmentPlan, planSelectedRemainingTotal, isTaksitli, selectedRemainingTotal, isSourceNote, sourceNoteRemaining]);

  const distributionPreview = useMemo(() => {
    if (isSourceInstallmentPlan) {
      // Plan mode: FIFO across selected installments
      if (effectiveAmount <= 0) return [];
      let remaining = effectiveAmount;
      const dist: { instId: string; label: string; allocated: number }[] = [];
      const ordered = [...customerPlanInstallments]
        .filter(i => selectedInstallmentIds.includes(i.id))
        .sort((a, b) => {
          if (a.status === 'Gecikti' && b.status !== 'Gecikti') return -1;
          if (a.status !== 'Gecikti' && b.status === 'Gecikti') return 1;
          return (a.due_date || '').localeCompare(b.due_date || '') || (a.sequence_no || 0) - (b.sequence_no || 0);
        });
      for (const inst of ordered) {
        if (remaining <= 0) break;
        const instRemaining = inst.remaining_amount !== undefined ? inst.remaining_amount : (inst.amount - (inst.paid_amount || 0));
        const alloc = Math.min(remaining, instRemaining);
        dist.push({ instId: inst.id, label: `${inst.sale_no || 'Satış'} - ${(inst.sequence_no ?? inst.installment_no ?? '?')}. Taksit`, allocated: alloc });
        remaining -= alloc;
      }
      return dist;
    }

    if (!isTaksitli || effectiveAmount <= 0) return [];
    let remaining = effectiveAmount;
    const dist: { instId: string; label: string; allocated: number }[] = [];
    const ordered = [...installments]
      .filter(i => selectedInstallmentIds.includes(i.id))
      .sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
    for (const inst of ordered) {
      if (remaining <= 0) break;
      const instRemaining = inst.remaining_amount !== undefined ? inst.remaining_amount : (inst.amount - (inst.paid_amount || 0));
      const alloc = Math.min(remaining, instRemaining);
      dist.push({ instId: inst.id, label: `${(inst.sequence_no ?? inst.installment_no ?? '?')}. Taksit`, allocated: alloc });
      remaining -= alloc;
    }
    return dist;
  }, [isSourceInstallmentPlan, isTaksitli, effectiveAmount, customerPlanInstallments, installments, selectedInstallmentIds]);

  // ─── Auto-select oldest unpaid ─────────────────────────
  useEffect(() => {
    const pool = isSourceInstallmentPlan ? planActiveInstallments : activeInstallments;
    const isTaksitliMode = isSourceInstallmentPlan || isTaksitli;
    if (!isTaksitliMode || pool.length === 0) return;
    if (selectedInstallmentIds.length > 0) return;
    const oldest = [...pool].sort((a, b) => {
      if (a.status === 'Gecikti' && b.status !== 'Gecikti') return -1;
      if (a.status !== 'Gecikti' && b.status === 'Gecikti') return 1;
      return (a.due_date || '').localeCompare(b.due_date || '');
    })[0];
    if (oldest) setSelectedInstallmentIds([oldest.id]);
  }, [isSourceInstallmentPlan, planActiveInstallments, isTaksitli, activeInstallments, selectedInstallmentIds.length]);

  // ─── Installment select helpers (works for both sale & plan mode) ────────
  const toggleInstallment = useCallback((id: string) => {
    setSelectedInstallmentIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const activeInstallmentPool = isSourceInstallmentPlan ? planActiveInstallments : activeInstallments;

  const toggleAll = useCallback(() => {
    setSelectedInstallmentIds(prev =>
      prev.length === activeInstallmentPool.length ? [] : activeInstallmentPool.map(i => i.id)
    );
  }, [activeInstallmentPool]);

  const selectOverdue = useCallback(() =>
    setSelectedInstallmentIds(activeInstallmentPool.filter(i => i.status === 'Gecikti').map(i => i.id)),
  [activeInstallmentPool]);

  const selectNextOnly = useCallback(() => {
    const oldest = [...activeInstallmentPool].sort((a, b) => {
      if (a.status === 'Gecikti' && b.status !== 'Gecikti') return -1;
      if (a.status !== 'Gecikti' && b.status === 'Gecikti') return 1;
      return (a.due_date || '').localeCompare(b.due_date || '');
    })[0];
    if (oldest) setSelectedInstallmentIds([oldest.id]);
  }, [activeInstallmentPool]);

  const selectThisMonth = useCallback(() => {
    const today = new Date().toISOString().slice(0, 7);
    setSelectedInstallmentIds(activeInstallmentPool.filter(i => (i.due_date || '').startsWith(today)).map(i => i.id));
  }, [activeInstallmentPool]);

  const selectOverdueUpToToday = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    setSelectedInstallmentIds(activeInstallmentPool.filter(i => i.status === 'Gecikti' || (i.due_date && i.due_date <= today && i.status !== 'Ödendi')).map(i => i.id));
  }, [activeInstallmentPool]);

  const fillFullRemaining = useCallback(() => {
    if (isSourceInstallmentPlan) {
      setCustomAmount(planSelectedRemainingTotal);
    } else if (isTaksitli) {
      setCustomAmount(selectedRemainingTotal);
    }
  }, [isSourceInstallmentPlan, planSelectedRemainingTotal, isTaksitli, selectedRemainingTotal]);

  const hasOverdueUnselected = activeInstallmentPool.some(i => i.status === 'Gecikti' && !selectedInstallmentIds.includes(i.id));

  // ─── Reset ──────────────────────────────────────────────
  const resetForm = useCallback(() => {
    setCustomerId(''); setSaleId(''); setSourceType('sale'); setSelectedNoteId('');
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentMethod('Nakit'); setDescription(''); setReceivedBy('Admin');
    setSelectedInstallmentIds([]); setCustomAmount(null); setErrors({});
    setShowSuccess(false); setShowOverrideWarning(false); setSaving(false);
    setDataLoaded(false); setNote(null); setInstallments([]); setCustomerNotes([]); setCustomerPlanInstallments([]);
    autoPickedForCustomer.current = '';
  }, []);

  // ─── Validation ─────────────────────────────────────────
  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (!effectiveCustomerId) errs.customerId = 'Müşteri seçiniz';
    // Sale required only for sale source type
    if (!isNoteMode && sourceType === 'sale' && !effectiveSaleId) errs.saleId = 'Satış seçiniz';
    // Note required for note source type (when not in existing note mode)
    if (!isNoteMode && sourceType === 'note' && !selectedNoteId) errs.sourceNote = 'Senet seçiniz';
    // Legacy debt: customer must have legacy (devir) debt specifically
    if (isSourceLegacyDebt && legacyDebtAmount <= 0) {
      errs.legacyDebt = 'Müşterinin eski sistem/devir borcu bulunmamaktadır';
    }
    if (isTaksitli && sourceType === 'sale' && selectedInstallmentIds.length === 0) errs.installments = 'En az bir taksit seçiniz';
    if (isSourceInstallmentPlan && selectedInstallmentIds.length === 0) errs.installments = 'En az bir taksit seçiniz';
    if (!effectiveAmount || effectiveAmount <= 0) errs.amount = 'Geçerli bir tutar giriniz';
    // Amount validation based on source
    if (isSourceNote && selectedSourceNote && effectiveAmount > sourceNoteRemaining) {
      errs.amount = 'Tutar kalan senet borcundan fazla olamaz';
    } else if (isSourceLegacyDebt && effectiveAmount > legacyDebtAmount) {
      errs.amount = 'Tutar eski sistem/devir borcundan fazla olamaz';
    } else if (!isNoteMode && !isSourceNote && !isSourceLegacyDebt && selectedSale && effectiveAmount > selectedSale.remaining_amount) {
      errs.amount = 'Tutar kalan borçtan fazla olamaz';
    }
    if (isTaksitli && sourceType === 'sale' && effectiveAmount > selectedRemainingTotal) errs.amount = 'Tutar seçili taksitlerin kalan toplamından fazla olamaz';
    if (!paymentDate) errs.paymentDate = 'Tarih seçiniz';
    else if (paymentDate > new Date().toISOString().slice(0, 10)) errs.paymentDate = 'Gelecek tarihli ödeme yapılamaz';
    if (isNoteMode && note) {
      const noteRemaining = (note as any).remaining_amount !== undefined ? (note as any).remaining_amount : (((note as any).amount || 0) - ((note as any).paid_amount || 0));
      if (effectiveAmount > noteRemaining) errs.amount = 'Tutar kalan senet borcundan fazla olamaz';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [effectiveCustomerId, effectiveSaleId, isNoteMode, isSourceInstallmentPlan, isTaksitli, selectedInstallmentIds, effectiveAmount, selectedSale, selectedRemainingTotal, paymentDate, note, sourceType, selectedNoteId, isSourceLegacyDebt, legacyDebtAmount, isSourceNote, selectedSourceNote, sourceNoteRemaining]);

  // ─── Submit ─────────────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (hasOverdueUnselected && !showOverrideWarning) { setShowOverrideWarning(true); return; }
    if (!validate() || saving) return;
    setSaving(true);
    try {
      let result: any;

      // Determine source_type for RPC
      if (isNoteMode && noteId) {
        // Existing note mode - pay directly to note
        result = await recordUnifiedPayment({
          source_type: 'note',
          customer_id: effectiveCustomerId,
          note_id: noteId,
          amount: effectiveAmount,
          payment_date: paymentDate,
          payment_method: paymentMethod,
          description: description.trim() || 'Senet ödemesi',
          received_by: receivedBy,
        });
      } else if (sourceType === 'note' && selectedNoteId) {
        // Note selected from source selector
        result = await recordUnifiedPayment({
          source_type: 'note',
          customer_id: effectiveCustomerId,
          note_id: selectedNoteId,
          amount: effectiveAmount,
          payment_date: paymentDate,
          payment_method: paymentMethod,
          description: description.trim() || 'Senet ödemesi',
          received_by: receivedBy,
        });
      } else if (isSourceLegacyDebt) {
        // Legacy debt payment
        result = await recordUnifiedPayment({
          source_type: 'legacy_debt',
          customer_id: effectiveCustomerId,
          amount: effectiveAmount,
          payment_date: paymentDate,
          payment_method: paymentMethod,
          description: description.trim() || 'Eski sistem borcu ödemesi',
          received_by: receivedBy,
        });
      } else if (isSourceInstallmentPlan) {
        // Taksit planı ödemesi - cross-sale dağıtım
        result = await recordUnifiedPayment({
          source_type: 'installment_plan',
          customer_id: effectiveCustomerId,
          amount: effectiveAmount,
          payment_date: paymentDate,
          payment_method: paymentMethod,
          description: description.trim() || 'Taksit planı ödemesi',
          received_by: receivedBy,
          installment_ids: selectedInstallmentIds,
        });
      } else {
        // Sale mode (peşin or taksitli)
        result = await recordUnifiedPayment({
          source_type: isTaksitli ? 'installment' : 'sale',
          customer_id: effectiveCustomerId,
          sale_id: effectiveSaleId,
          amount: effectiveAmount,
          payment_date: paymentDate,
          payment_method: paymentMethod,
          description: description.trim(),
          received_by: receivedBy,
          installment_ids: isTaksitli ? selectedInstallmentIds : undefined,
        });
      }

      if (result.error) {
        setErrors({ submit: toFriendlyPaymentError(result.error) });
        setSaving(false);
        return;
      }

      // Ödeme sözlerini otomatik senkronize et
      const paymentId = result.data?.id;
      if (paymentId && effectiveCustomerId && effectiveAmount > 0) {
        try {
          await syncPaymentPromisesAfterPayment(paymentId, effectiveCustomerId, effectiveAmount);
        } catch { /* sync hatası ödeme akışını engellemez */ }
      }

      // Post-payment: enforcement case update for note mode
      if (isNoteMode && noteId && enforcementCaseId) {
        try {
          const ecRes = await getEnforcementCaseById(enforcementCaseId);
          const ec = ecRes.data;
          if (ec) {
            const newPaid = parseFloat(ec.paid_after_enforcement || 0) + effectiveAmount;
            const newRemaining = Math.max(0, parseFloat(ec.total_debt || 0) - newPaid);
            await updateEnforcementCase(enforcementCaseId, {
              paid_after_enforcement: newPaid,
              remaining_debt: newRemaining,
              status: newRemaining <= 0 ? 'Tahsil Edildi' : 'Kısmi Ödeme',
            });
          }
        } catch { /* silent */ }
      }

      const receiptNo = result.data?.receipt_no || '—';

      setShowSuccess(true);
      triggerRefresh();
      if (onPaymentCreated) onPaymentCreated();
      showToast(`Ödeme başarıyla kaydedildi. Makbuz No: ${receiptNo}`, 'success');
      setTimeout(() => { resetForm(); onClose(); }, 1500);
    } catch {
      setErrors({ submit: 'Ödeme kaydedilirken bir hata oluştu.' });
      setSaving(false);
    }
  }, [hasOverdueUnselected, showOverrideWarning, validate, saving, isNoteMode, noteId, sourceType, selectedNoteId, isSourceLegacyDebt, isSourceInstallmentPlan, isTaksitli, effectiveCustomerId, effectiveSaleId, effectiveAmount, paymentDate, paymentMethod, description, receivedBy, selectedInstallmentIds, enforcementCaseId, triggerRefresh, onPaymentCreated, showToast, resetForm, onClose]);

  if (!open) return null;

  const handleClose = () => { if (saving) return; resetForm(); onClose(); };

  // ─── Input classes ──────────────────────────────────────
  const inputClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 placeholder:text-foreground-300';
  const selectClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 cursor-pointer';
  const labelClass = 'text-xs font-medium text-foreground-500 mb-1.5 block';

  // ─── Success banner ────────────────────────────────────
  const successBanner = showSuccess ? (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mx-5 mt-4">
      <h4 className="font-semibold text-emerald-700">Ödeme başarıyla kaydedildi!</h4>
      <p className="text-sm text-emerald-500 mt-0.5">{formatTRY(effectiveAmount)} tutarındaki ödeme işlendi.</p>
    </div>
  ) : null;

  // ─── Override warning ──────────────────────────────────
  const overrideWarning = showOverrideWarning ? (
    <div className="fixed inset-0 bg-black/40 z-[120] flex items-center justify-center p-4" onClick={() => setShowOverrideWarning(false)}>
      <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4 mx-auto"><i className="ri-error-warning-line text-xl text-amber-500"></i></div>
        <h3 className="text-lg font-semibold text-foreground-900 text-center mb-2">Önceki Taksitler Ödenmemiş</h3>
        <p className="text-sm text-foreground-500 text-center mb-6">Önceki ödenmemiş taksitler bulunmaktadır. Yine de devam etmek istiyor musunuz?</p>
        <div className="flex gap-3">
          <button onClick={() => setShowOverrideWarning(false)} className="flex-1 btn-outline text-center cursor-pointer">İptal</button>
          <button onClick={() => { setShowOverrideWarning(false); setTimeout(() => handleSubmit(), 50); }} className="flex-1 bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer">Devam Et</button>
        </div>
      </div>
    </div>
  ) : null;

  // ─── Form Body ─────────────────────────────────────────
  const formBody = (
    <div className="px-5 py-4 space-y-4">
      {/* Error banner */}
      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{errors.submit}</div>
      )}

      {/* Customer selection */}
      {isLockedCustomer ? (
        selectedCustomer ? (
          <div>
            <label className={labelClass}>Müşteri</label>
            <div className="bg-background-100/50 rounded-lg border border-background-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><p className="text-xs text-foreground-400">Ad Soyad</p><p className="text-sm font-medium">{selectedCustomer.full_name}</p></div>
              <div><p className="text-xs text-foreground-400">Telefon</p><p className="text-sm font-medium">{selectedCustomer.phone}</p></div>
              <div><p className="text-xs text-foreground-400">Toplam Borç</p><p className={`text-sm font-bold ${selectedCustomer.total_debt_amount > 0 ? 'text-red-500' : 'text-emerald-600'}`}>{formatTRY(selectedCustomer.total_debt_amount)}</p></div>
              <div><p className="text-xs text-foreground-400">Durum</p><p className="text-sm font-medium">{selectedCustomer.status}</p></div>
            </div>
          </div>
        ) : (
          <div className="bg-background-100/50 rounded-lg border border-background-200 p-4 text-center">
            <p className="text-sm text-foreground-400">Müşteri bilgisi yüklenemedi.</p>
          </div>
        )
      ) : (
        <div>
          <CustomerSearchSelect
            value={customerId}
            onChange={(id) => { setCustomerId(id); setSaleId(''); setErrors({}); }}
            label="Müşteri"
            required
            error={errors.customerId}
            placeholder="Müşteri ara..."
          />
          {selectedCustomer && (
            <div className="mt-2 bg-background-100/50 rounded-lg border border-background-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><p className="text-xs text-foreground-400">Toplam Borç</p><p className={`text-sm font-bold ${selectedCustomer.total_debt_amount > 0 ? 'text-red-500' : 'text-emerald-600'}`}>{formatTRY(selectedCustomer.total_debt_amount)}</p></div>
              <div><p className="text-xs text-foreground-400">Durum</p><p className="text-sm font-medium">{selectedCustomer.status}</p></div>
            </div>
          )}
        </div>
      )}

      {/* ─── Source Type Selector (only in general/customer modes) ─── */}
      {showSourceSelector && effectiveCustomerId && dataLoaded && sourceDataReady && (
        <div>
          <label className={labelClass}>Ödeme Kaynağı <span className="text-red-400">*</span></label>
          {!hasAnyPayableSource ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 flex items-center gap-2">
              <i className="ri-error-warning-line"></i>
              Bu müşteride ödenebilir açık kayıt bulunamadı.
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              <button
                type="button"
                onClick={() => { setSourceType('sale'); setSelectedNoteId(''); setCustomAmount(null); setErrors({}); }}
                disabled={customerSales.length === 0}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${customerSales.length === 0 ? 'opacity-40 cursor-not-allowed' : ''} ${sourceType === 'sale' ? 'border-primary-400 bg-primary-50' : 'border-background-200 bg-background-50 hover:border-background-300'}`}
              >
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${sourceType === 'sale' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}>
                  <i className="ri-shopping-bag-line text-sm"></i>
                </span>
                <span className={`text-xs font-medium ${sourceType === 'sale' ? 'text-primary-700' : 'text-foreground-600'}`}>Satış</span>
                {customerSales.length > 0 && (
                  <span className="text-[10px] text-foreground-400">{customerSales.length} satış</span>
                )}
              </button>
              <button
                type="button"
                onClick={() => { setSourceType('note'); setSaleId(''); setSelectedNoteId(''); setCustomAmount(null); setErrors({}); }}
                disabled={customerNotes.length === 0}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${customerNotes.length === 0 ? 'opacity-40 cursor-not-allowed' : ''} ${sourceType === 'note' ? 'border-primary-400 bg-primary-50' : 'border-background-200 bg-background-50 hover:border-background-300'}`}
              >
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${sourceType === 'note' ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}>
                  <i className="ri-file-text-line text-sm"></i>
                </span>
                <span className={`text-xs font-medium ${sourceType === 'note' ? 'text-primary-700' : 'text-foreground-600'}`}>Senet</span>
                {customerNotes.length > 0 && (
                  <span className="text-[10px] text-foreground-400">{customerNotes.length} açık</span>
                )}
              </button>
              <button
                type="button"
                onClick={() => { setSourceType('installment_plan'); setSaleId(''); setSelectedNoteId(''); setCustomAmount(null); setSelectedInstallmentIds([]); setErrors({}); }}
                disabled={planOpenCount === 0}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${planOpenCount === 0 ? 'opacity-40 cursor-not-allowed' : ''} ${sourceType === 'installment_plan' ? 'border-accent-400 bg-accent-50' : 'border-background-200 bg-background-50 hover:border-background-300'}`}
              >
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${sourceType === 'installment_plan' ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}>
                  <i className="ri-calendar-check-line text-sm"></i>
                </span>
                <span className={`text-xs font-medium ${sourceType === 'installment_plan' ? 'text-accent-700' : 'text-foreground-600'}`}>Taksit Planı</span>
                {planOpenCount > 0 && (
                  <span className="text-[10px] text-foreground-400">{planOpenCount} açık taksit</span>
                )}
              </button>
              <button
                type="button"
                onClick={() => { setSourceType('legacy_debt'); setSaleId(''); setSelectedNoteId(''); setCustomAmount(null); setErrors({}); }}
                disabled={!hasLegacyDebt}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${!hasLegacyDebt ? 'opacity-40 cursor-not-allowed' : ''} ${sourceType === 'legacy_debt' ? 'border-amber-400 bg-amber-50' : 'border-background-200 bg-background-50 hover:border-background-300'}`}
              >
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${sourceType === 'legacy_debt' ? 'bg-amber-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}>
                  <i className="ri-history-line text-sm"></i>
                </span>
                <span className={`text-xs font-medium ${sourceType === 'legacy_debt' ? 'text-amber-700' : 'text-foreground-600'}`}>Eski Sistem Borcu</span>
                {hasLegacyDebt && (
                  <span className="text-[10px] text-foreground-400">{formatTRY(legacyDebtAmount)} devir borcu</span>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Note info for note mode */}
      {isNoteMode && note && (
        <div>
          <label className={labelClass}>Senet Bilgisi</label>
          <div className="bg-background-100/50 rounded-lg border border-background-200 p-4 grid grid-cols-3 gap-3">
            <div><p className="text-xs text-foreground-400">Senet No</p><p className="text-sm font-medium text-primary-600">{(note as any).note_no}</p></div>
            <div><p className="text-xs text-foreground-400">Senet Tutarı</p><p className="text-sm font-medium">{formatTRY((note as any).amount || 0)}</p></div>
            <div>
              <p className="text-xs text-foreground-400">Kalan Borç</p>
              <p className={`text-sm font-bold ${((note as any).remaining_amount !== undefined ? (note as any).remaining_amount : (((note as any).amount || 0) - ((note as any).paid_amount || 0))) > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                {formatTRY((note as any).remaining_amount !== undefined ? (note as any).remaining_amount : (((note as any).amount || 0) - ((note as any).paid_amount || 0)))}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ─── Note Selection (when source type is 'note' from selector) ─── */}
      {!isNoteMode && sourceType === 'note' && customerNotes.length > 0 && (
        <div>
          <label className={labelClass}>Senet Seçimi <span className="text-red-400">*</span></label>
          <select
            value={selectedNoteId}
            onChange={e => { setSelectedNoteId(e.target.value); setErrors({}); setCustomAmount(null); }}
            className={`${selectClass} ${errors.sourceNote ? 'border-red-400' : ''}`}
          >
            <option value="">Senet seçiniz</option>
            {customerNotes.map((n: any) => {
              const rem = n.remaining_amount !== undefined ? n.remaining_amount : (n.amount - (n.paid_amount || 0));
              const dueLabel = n.due_date ? new Date(n.due_date).toLocaleDateString('tr-TR') : 'Vade yok';
              return (
                <option key={n.id} value={n.id}>
                  {n.note_no} | Tutar: {formatTRY(n.amount)} | Kalan: {formatTRY(rem)} | Vade: {dueLabel} | {n.status}
                </option>
              );
            })}
          </select>
          {errors.sourceNote && <p className="text-xs text-red-500 mt-1">{errors.sourceNote}</p>}

          {selectedSourceNote && (
            <div className="mt-2 bg-background-100/50 rounded-lg border border-background-200 p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
              <div><p className="text-xs text-foreground-400">Senet No</p><p className="text-sm font-medium text-primary-600">{(selectedSourceNote as any).note_no}</p></div>
              <div><p className="text-xs text-foreground-400">Toplam Tutar</p><p className="text-sm font-medium">{formatTRY((selectedSourceNote as any).amount || 0)}</p></div>
              <div><p className="text-xs text-foreground-400">Ödenen</p><p className="text-sm font-medium text-emerald-600">{formatTRY((selectedSourceNote as any).paid_amount || 0)}</p></div>
              <div><p className="text-xs text-foreground-400">Kalan Borç</p><p className={`text-sm font-bold ${sourceNoteRemaining > 0 ? 'text-red-500' : 'text-emerald-600'}`}>{formatTRY(sourceNoteRemaining)}</p></div>
              <div>
                <p className="text-xs text-foreground-400">Vade & Durum</p>
                <p className="text-sm font-medium">
                  {(selectedSourceNote as any).due_date ? new Date((selectedSourceNote as any).due_date).toLocaleDateString('tr-TR') : '—'}
                  <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${(selectedSourceNote as any).status === 'Gecikti' ? 'bg-red-100 text-red-700' : (selectedSourceNote as any).status === 'Kısmi Ödendi' ? 'bg-amber-100 text-amber-700' : 'bg-background-200 text-foreground-600'}`}>
                    {(selectedSourceNote as any).status}
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Installment Plan Selection ─── */}
      {isSourceInstallmentPlan && customerPlanInstallments.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground-700">Taksit Planı Seçimi <span className="text-red-400">*</span></label>
            <span className="text-xs text-foreground-400">{planOpenCount} açık taksit</span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={selectNextOnly} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-accent-50 hover:text-accent-600 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-skip-forward-line mr-1"></i>Sıradaki
            </button>
            <button type="button" onClick={selectOverdue} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-red-50 hover:text-red-600 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-alert-line mr-1"></i>Gecikenler
            </button>
            <button type="button" onClick={selectOverdueUpToToday} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-amber-50 hover:text-amber-600 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-timer-line mr-1"></i>Vadesi Gelenler
            </button>
            <button type="button" onClick={toggleAll} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-accent-50 hover:text-accent-600 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors">
              {selectedInstallmentIds.length === activeInstallmentPool.length ? (
                <><i className="ri-checkbox-blank-line mr-1"></i>Seçimi Temizle</>
              ) : (
                <><i className="ri-checkbox-multiple-line mr-1"></i>Tümünü Seç</>
              )}
            </button>
          </div>

          <div className="space-y-2 max-h-[320px] overflow-y-auto">
            {plansortedInstallments.map((inst) => {
              const isSelected = selectedInstallmentIds.includes(inst.id);
              const instRemaining = inst.remaining_amount !== undefined ? inst.remaining_amount : (inst.amount - (inst.paid_amount || 0));
              const instPaid = inst.paid_amount || 0;
              return (
                <div key={inst.id}
                  className={`rounded-xl border p-3 flex items-center gap-3 transition-colors cursor-pointer ${isSelected ? 'bg-accent-50 border-accent-300' : 'bg-background-50 border-background-200 hover:bg-background-100'}`}
                  onClick={() => toggleInstallment(inst.id)}>
                  <input type="checkbox" checked={isSelected} onChange={() => {}}
                    className="w-4 h-4 rounded border-background-300 text-accent-500 focus:ring-accent-400 cursor-pointer flex-shrink-0" />
                  <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-8 gap-2 items-center">
                    <div className="sm:col-span-1"><p className="text-xs text-foreground-400">#{inst.sequence_no ?? inst.installment_no ?? '?'}</p></div>
                    <div className="sm:col-span-1"><p className="text-xs text-foreground-500">{inst.due_date ? new Date(inst.due_date).toLocaleDateString('tr-TR') : '—'}</p></div>
                    <div className="sm:col-span-1"><p className="text-xs font-medium text-foreground-800">{formatTRY(inst.amount)}</p></div>
                    <div className="sm:col-span-1"><p className="text-xs text-foreground-500">{instPaid > 0 ? formatTRY(instPaid) : '—'}</p></div>
                    <div className="sm:col-span-1"><p className={`text-xs font-bold ${instRemaining > 0 ? 'text-red-500' : 'text-emerald-600'}`}>{instRemaining > 0 ? formatTRY(instRemaining) : '—'}</p></div>
                    <div className="sm:col-span-1"><Badge badgeClass={installmentStatus.badgeClass(inst.status)} icon={installmentStatus.icon(inst.status)}>{inst.status}</Badge></div>
                    <div className="sm:col-span-1">
                      {inst.linked_note_no ? (
                        <p className="text-[10px] text-foreground-400 truncate" title={inst.linked_note_no}>{inst.linked_note_no}</p>
                      ) : inst.sale_no ? (
                        <p className="text-[10px] text-foreground-400 truncate" title={inst.sale_no}>{inst.sale_no}{inst.is_legacy_sale ? ' (D)' : ''}</p>
                      ) : (
                        <p className="text-[10px] text-foreground-300">—</p>
                      )}
                    </div>
                    <div className="sm:col-span-1">
                      <p className="text-[10px] text-foreground-400 truncate">{inst.description || ''}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {errors.installments && <p className="text-xs text-red-500">{errors.installments}</p>}
          {selectedInstallmentIds.length > 0 && (
            <p className="text-xs text-foreground-400">
              Seçilen taksitlerin kalan toplamı: <span className="font-semibold text-foreground-700">{formatTRY(planSelectedRemainingTotal)}</span>
            </p>
          )}
        </div>
      )}

      {/* ─── Legacy Debt Info ─── */}
      {isSourceLegacyDebt && selectedCustomer && (
        <div>
          <label className={labelClass}>Eski Sistem Borcu Bilgisi</label>
          <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <p className="text-xs text-amber-600">Müşteri</p>
                <p className="text-sm font-medium text-foreground-800">{selectedCustomer.full_name}</p>
              </div>
              <div>
                <p className="text-xs text-amber-600">Devir Borcu</p>
                <p className={`text-sm font-bold ${legacyDebtAmount > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                  {formatTRY(legacyDebtAmount)}
                </p>
              </div>
              <div>
                <p className="text-xs text-amber-600">Durum</p>
                <p className="text-sm font-medium">{selectedCustomer.status}</p>
              </div>
            </div>
            <p className="text-xs text-amber-500 mt-3 pt-2 border-t border-amber-200">
              <i className="ri-information-line mr-1"></i>
              Ödeme, müşteriye ait açık eski sistem (devir) senetlerinden düşülecektir. Kasa hareketi oluşturulacaktır.
            </p>
          </div>
          {errors.legacyDebt && <p className="text-xs text-red-500 mt-1">{errors.legacyDebt}</p>}
        </div>
      )}

      {/* Sale selection (non-note mode, only when source is 'sale') */}
      {!isNoteMode && (!showSourceSelector || (sourceType === 'sale' && customerSales.length > 0)) && (
        <>
          {isLockedSale ? (
            selectedSale ? (
              <div>
                <label className={labelClass}>İlgili Satış</label>
                <div className="bg-background-100/50 rounded-lg border border-background-200 p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div><p className="text-xs text-foreground-400">Satış No</p><p className="text-sm font-medium text-primary-600">{selectedSale.sale_no}</p></div>
                  <div><p className="text-xs text-foreground-400">Toplam</p><p className="text-sm font-medium">{formatTRY(selectedSale.total_amount)}</p></div>
                  <div><p className="text-xs text-foreground-400">Ödenen</p><p className="text-sm font-medium text-emerald-600">{formatTRY(selectedSale.paid_amount)}</p></div>
                  <div><p className="text-xs text-foreground-400">Kalan</p><p className="text-sm font-bold text-red-500">{formatTRY(selectedSale.remaining_amount)}</p></div>
                  <div><p className="text-xs text-foreground-400">Ödeme Tipi</p><p className={`text-sm font-medium ${isTaksitli ? 'text-accent-600' : ''}`}>{selectedSale.payment_type}</p></div>
                </div>
              </div>
            ) : (
              <div className="bg-background-100/50 rounded-lg border border-background-200 p-4 text-center">
                <p className="text-sm text-foreground-400">Satış bilgisi yüklenemedi.</p>
              </div>
            )
          ) : (
            <div>
              <label className={labelClass}>İlgili Satış <span className="text-red-400">*</span></label>
              <select value={saleId} onChange={e => { setSaleId(e.target.value); setErrors({}); }}
                className={`${selectClass} ${errors.saleId ? 'border-red-400' : ''}`} disabled={!effectiveCustomerId}>
                <option value="">Satış seçiniz</option>
                {customerSales.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.sale_no} | Toplam: {formatTRY(s.total_amount)} | Ödenen: {formatTRY(s.paid_amount)} | Kalan: {formatTRY(s.remaining_amount)} | {s.payment_type}
                  </option>
                ))}
              </select>
              {errors.saleId && <p className="text-xs text-red-500 mt-1">{errors.saleId}</p>}
              {selectedSale && (
                <div className="mt-2 bg-background-100/50 rounded-lg border border-background-200 p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div><p className="text-xs text-foreground-400">Satış No</p><p className="text-sm font-medium text-primary-600">{selectedSale.sale_no}</p></div>
                  <div><p className="text-xs text-foreground-400">Toplam</p><p className="text-sm font-medium">{formatTRY(selectedSale.total_amount)}</p></div>
                  <div><p className="text-xs text-foreground-400">Ödenen</p><p className="text-sm font-medium text-emerald-600">{formatTRY(selectedSale.paid_amount)}</p></div>
                  <div><p className="text-xs text-foreground-400">Kalan</p><p className="text-sm font-bold text-red-500">{formatTRY(selectedSale.remaining_amount)}</p></div>
                  <div><p className="text-xs text-foreground-400">Ödeme Tipi</p><p className={`text-sm font-medium ${isTaksitli ? 'text-accent-600' : ''}`}>{selectedSale.payment_type}</p></div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Installment selection (only for taksitli) */}
      {isTaksitli && installments.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground-700">Taksit Seçimi <span className="text-red-400">*</span></label>
            <span className="text-xs text-foreground-400">{installments.length} taksit</span>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <button type="button" onClick={selectNextOnly} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-primary-50 hover:text-primary-600 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-skip-forward-line mr-1"></i>Sıradaki
            </button>
            <button type="button" onClick={selectOverdue} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-red-50 hover:text-red-600 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-alert-line mr-1"></i>Gecikenleri
            </button>
            <button type="button" onClick={selectThisMonth} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-accent-50 hover:text-accent-600 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-calendar-line mr-1"></i>Bu Ayın
            </button>
            <button type="button" onClick={toggleAll} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-accent-50 hover:text-accent-600 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors">
              {selectedInstallmentIds.length === activeInstallments.length ? (
                <><i className="ri-checkbox-blank-line mr-1"></i>Seçimi Temizle</>
              ) : (
                <><i className="ri-checkbox-multiple-line mr-1"></i>Tümünü Seç</>
              )}
            </button>
          </div>

          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {sortedInstallments.map((inst) => {
              const isSelected = selectedInstallmentIds.includes(inst.id);
              const isDisabled = inst.status === 'Ödendi' || inst.status === 'İptal';
              const instRemaining = inst.remaining_amount !== undefined ? inst.remaining_amount : (inst.amount - (inst.paid_amount || 0));
              const instPaid = inst.paid_amount || 0;
              return (
                <div key={inst.id}
                  className={`rounded-xl border p-3 flex items-center gap-3 transition-colors cursor-pointer ${isDisabled ? 'opacity-50 cursor-not-allowed bg-background-50' : isSelected ? 'bg-primary-50 border-primary-300' : 'bg-background-50 border-background-200 hover:bg-background-100'}`}
                  onClick={() => !isDisabled && toggleInstallment(inst.id)}>
                  <input type="checkbox" checked={isSelected} onChange={() => {}} disabled={isDisabled}
                    className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer flex-shrink-0" />
                  <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-6 gap-2 items-center">
                    <div className="sm:col-span-1"><p className="text-xs text-foreground-400">#{inst.sequence_no ?? inst.installment_no ?? '?'}</p></div>
                    <div className="sm:col-span-1"><p className="text-xs text-foreground-500">{inst.due_date ? new Date(inst.due_date).toLocaleDateString('tr-TR') : '—'}</p></div>
                    <div className="sm:col-span-1"><p className="text-xs font-medium text-foreground-800">{formatTRY(inst.amount)}</p></div>
                    <div className="sm:col-span-1"><p className="text-xs text-foreground-500">{instPaid > 0 ? formatTRY(instPaid) : '—'}</p></div>
                    <div className="sm:col-span-1"><p className={`text-xs font-bold ${instRemaining > 0 ? 'text-red-500' : 'text-emerald-600'}`}>{instRemaining > 0 ? formatTRY(instRemaining) : '—'}</p></div>
                    <div className="sm:col-span-1"><Badge badgeClass={installmentStatus.badgeClass(inst.status)} icon={installmentStatus.icon(inst.status)}>{inst.status}</Badge></div>
                  </div>
                </div>
              );
            })}
          </div>
          {errors.installments && <p className="text-xs text-red-500">{errors.installments}</p>}
          {selectedInstallmentIds.length > 0 && (
            <p className="text-xs text-foreground-400">
              Seçilen taksitlerin kalan toplamı: <span className="font-semibold text-foreground-700">{formatTRY(selectedRemainingTotal)}</span>
            </p>
          )}
        </div>
      )}

      {/* Payment amount */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Ödeme Tutarı (TL) <span className="text-red-400">*</span></label>
          <CurrencyInput
            min={0}
            max={isSourceInstallmentPlan ? planSelectedRemainingTotal : (isTaksitli ? selectedRemainingTotal : (isSourceNote ? sourceNoteRemaining : (isSourceLegacyDebt ? legacyDebtAmount : (selectedSale?.remaining_amount || selectedCustomer?.total_debt_amount || 0))))}
            value={customAmount !== null ? String(customAmount || '') : (isSourceInstallmentPlan ? String(planSelectedRemainingTotal || '') : (isTaksitli ? String(selectedRemainingTotal || '') : (isSourceNote ? String(sourceNoteRemaining || '') : '')))}
            onChange={v => setCustomAmount(v ? parseFloat(v) : null)}
            className={`${inputClass} ${errors.amount ? 'border-red-400' : ''}`}
            placeholder={isSourceInstallmentPlan ? 'Taksit planı toplamı...' : isTaksitli ? 'Otomatik hesaplanıyor...' : isSourceNote ? 'Senet kalan borcu' : isSourceLegacyDebt ? 'Tutar giriniz' : 'Tutar giriniz'} />
          {errors.amount && <p className="text-xs text-red-500 mt-1">{errors.amount}</p>}
          {isSourceInstallmentPlan && planSelectedRemainingTotal > 0 && (
            <p className="text-xs text-foreground-400 mt-1">Varsayılan: seçili taksitlerin kalan toplamı ({formatTRY(planSelectedRemainingTotal)}). Kısmi ödeme yapabilirsiniz.</p>
          )}
          {isTaksitli && selectedRemainingTotal > 0 && (
            <p className="text-xs text-foreground-400 mt-1">Varsayılan: seçili taksitlerin kalan toplamı ({formatTRY(selectedRemainingTotal)}). Kısmi ödeme yapabilirsiniz.</p>
          )}
          {/* Quick buttons for note source (from selector) */}
          {!isNoteMode && sourceType === 'note' && selectedSourceNote && (
            <div className="flex gap-2 mt-1.5">
              <button type="button" onClick={() => setCustomAmount(sourceNoteRemaining)}
                className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md hover:bg-emerald-100 cursor-pointer whitespace-nowrap">Tamamını Öde</button>
              <button type="button" onClick={() => setCustomAmount(Math.ceil(sourceNoteRemaining / 2))}
                className="text-[10px] px-2 py-0.5 bg-background-100 text-foreground-500 rounded-md hover:bg-background-200 cursor-pointer whitespace-nowrap">Yarısını Öde</button>
            </div>
          )}
          {/* Quick buttons for installment plan */}
          {isSourceInstallmentPlan && planSelectedRemainingTotal > 0 && (
            <div className="flex gap-2 mt-1.5">
              <button type="button" onClick={() => setCustomAmount(planSelectedRemainingTotal)}
                className="text-[10px] px-2 py-0.5 bg-accent-50 text-accent-700 rounded-md hover:bg-accent-100 cursor-pointer whitespace-nowrap">Tamamını Öde</button>
              <button type="button" onClick={() => {
                setCustomAmount(planOverdueTotal);
                selectOverdueUpToToday();
              }}
                className="text-[10px] px-2 py-0.5 bg-amber-50 text-amber-700 rounded-md hover:bg-amber-100 cursor-pointer whitespace-nowrap">Vadesi Gelenleri Öde</button>
            </div>
          )}
          {/* Quick buttons for legacy debt */}
          {isSourceLegacyDebt && legacyDebtAmount > 0 && (
            <div className="flex gap-2 mt-1.5">
              <button type="button" onClick={() => setCustomAmount(legacyDebtAmount)}
                className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md hover:bg-emerald-100 cursor-pointer whitespace-nowrap">Tamamını Öde</button>
              <button type="button" onClick={() => setCustomAmount(Math.ceil(legacyDebtAmount / 2))}
                className="text-[10px] px-2 py-0.5 bg-background-100 text-foreground-500 rounded-md hover:bg-background-200 cursor-pointer whitespace-nowrap">Yarısını Öde</button>
            </div>
          )}
          {isNoteMode && note && (
            <div className="flex gap-2 mt-1.5">
              <button type="button" onClick={() => {
                const nRem = (note as any).remaining_amount !== undefined ? (note as any).remaining_amount : (((note as any).amount || 0) - ((note as any).paid_amount || 0));
                setCustomAmount(nRem);
              }} className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md hover:bg-emerald-100 cursor-pointer whitespace-nowrap">Tamamını Öde</button>
              <button type="button" onClick={() => {
                const nRem = (note as any).remaining_amount !== undefined ? (note as any).remaining_amount : (((note as any).amount || 0) - ((note as any).paid_amount || 0));
                setCustomAmount(Math.ceil(nRem / 2));
              }} className="text-[10px] px-2 py-0.5 bg-background-100 text-foreground-500 rounded-md hover:bg-background-200 cursor-pointer whitespace-nowrap">Yarısını Öde</button>
            </div>
          )}
        </div>
        <div>
          <label className={labelClass}>Yöntem</label>
          <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className={selectClass}>
            {methods.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {/* Distribution preview */}
      {(isSourceInstallmentPlan || isTaksitli) && distributionPreview.length > 0 && effectiveAmount > 0 && (
        <div className="bg-accent-50 border border-accent-200 rounded-xl p-4">
          <h4 className="text-sm font-semibold text-accent-800 mb-3">Taksit Dağılımı Ön İzlemesi</h4>
          <p className="text-xs text-accent-600 mb-2">Ödeme: <span className="font-semibold">{formatTRY(effectiveAmount)}</span></p>
          <div className="space-y-1.5">
            {distributionPreview.map((d, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="text-foreground-600">{d.label}</span>
                <span className="font-medium text-emerald-600">{formatTRY(d.allocated)}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-accent-500 mt-2 pt-2 border-t border-accent-200">İşlem sonrası taksit durumları güncellenecektir.</p>
        </div>
      )}

      {/* Payment details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Tarih <span className="text-red-400">*</span></label>
          <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)}
            className={`${inputClass} ${errors.paymentDate ? 'border-red-400' : ''}`}
            max={new Date().toISOString().slice(0, 10)} />
          {errors.paymentDate && <p className="text-xs text-red-500 mt-1">{errors.paymentDate}</p>}
        </div>
        <div>
          <label className={labelClass}>Ödemeyi Alan</label>
          <select value={receivedBy} onChange={e => setReceivedBy(e.target.value)} className={selectClass}>
            {personnel.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className={labelClass}>Makbuz No</label>
        <div className="w-full px-3 py-2.5 text-sm sm:text-base bg-background-50 border border-background-200 rounded-lg text-foreground-400">
          <span className="italic">Otomatik oluşturulacak</span>
        </div>
      </div>

      <div>
        <label className={labelClass}>Açıklama</label>
        <input type="text" value={description} onChange={e => setDescription(e.target.value)}
          className={inputClass} placeholder="Opsiyonel açıklama" />
      </div>

      <div className="h-4 sm:hidden" />
    </div>
  );

  // ─── Page mode ──────────────────────────────────────────
  if (asPage) {
    return (
      <div className="max-w-3xl mx-auto space-y-5">
        {overrideWarning}
        {successBanner}
        <div className="flex items-center gap-4">
          <button onClick={handleClose} className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200"><i className="ri-arrow-left-line text-foreground-600"></i></button>
          <div><h2 className="text-xl font-bold text-foreground-900 font-heading">Yeni Ödeme Ekle</h2></div>
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200/70">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div>
            </div>
          ) : (
            <>
              {formBody}
              <div className="flex gap-3 px-5 py-3 border-t border-background-100">
                <button onClick={handleClose} className="flex-1 btn-outline text-center cursor-pointer" disabled={saving}>İptal</button>
                <button onClick={handleSubmit} disabled={saving} className="flex-1 btn-primary flex items-center justify-center gap-2 text-center cursor-pointer whitespace-nowrap disabled:opacity-50">
                  {saving ? <><i className="ri-loader-4-line animate-spin"></i>Kaydediliyor...</> : <><i className="ri-check-line"></i>Ödemeyi Kaydet</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── Modal mode ─────────────────────────────────────────
  const headerContent = (
    <div className="flex items-center justify-between px-5 py-3.5">
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <i className="ri-bank-card-line text-background-50 text-sm"></i>
        </span>
        <div>
          <h3 className="text-base font-semibold text-foreground-900">
            {isNoteMode ? 'Senet Ödemesi Al' : mode === 'customer' ? 'Müşteriden Ödeme Al' : 'Ödeme Ekle'}
          </h3>
          {isNoteMode && note && <p className="text-xs text-foreground-400">{(note as any).note_no}</p>}
          {mode === 'customer' && selectedCustomer && <p className="text-xs text-foreground-400">{selectedCustomer.full_name} için ödeme</p>}
        </div>
      </div>
      <button onClick={handleClose} disabled={saving} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer disabled:opacity-50">
        <i className="ri-close-line text-foreground-500"></i>
      </button>
    </div>
  );

  const footerContent = (
    <div className="flex gap-3 px-5 py-3">
      <button onClick={handleClose} disabled={saving} className="btn-outline cursor-pointer whitespace-nowrap disabled:opacity-50 text-sm">İptal</button>
      <button onClick={handleSubmit} disabled={saving} className="btn-primary flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60 text-sm flex-1 lg:flex-none">
        {saving ? <><i className="ri-loader-4-line animate-spin"></i>Kaydediliyor...</> : <><i className="ri-check-line"></i>Ödemeyi Kaydet</>}
      </button>
    </div>
  );

  return (
    <>
      {overrideWarning}
      <ResponsiveFormModal
        isOpen={open}
        onClose={handleClose}
        zIndex={60}
        header={headerContent}
        footer={footerContent}
      >
        {successBanner}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></div>
          </div>
        ) : formBody}
      </ResponsiveFormModal>
    </>
  );
}